// src/features/task/TaskTypeForm.tsx
// Shared body for TaskTypeCreatePage / TaskTypeEditPage.
//
// Follows the settings house style used by /settings/tasks: cards, each with
// its own heading and a one-line description, labels above their field, fields
// full width inside a two-column grid. It replaces the label-left column layout
// this form used to have, which was a layout language no other settings page
// spoke, and the mismatched field widths that came with it.
//
// Product id is not a field: it is derived from the current scope, so it is
// context rather than something a person types.

import { useEffect, useRef } from 'react';
import { observer } from '@react';
import { useI18n, useTaskCustomField } from '@react/hooks';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Input } from '@react/components';
import { Select } from '@/components/Select';
import { IconPicker } from '@/components/IconPicker';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { UserPicker } from '@/components/UserPicker';
import { RolePicker } from '@/components/RolePicker';
import { PipelinePicker } from '@/components/PipelinePicker';
import { TaskTemplatePicker } from '@/components/TaskTemplatePicker';
import { ChipMultiSelect } from '@/features/task/ChipMultiSelect';
import { InheritedValueNote, inheritedPlaceholder } from '@/features/task/InheritanceNotice';
import { parseTags } from '@/features/task/logic';
import type { Entity } from '@core/types/entity';
import type {
  ResolvedReference,
  TaskAssignmentRule,
  TaskStatusDefinition,
  TaskTransitionRule,
  TaskTypeProperty,
} from '@core/types/task';

export const TASK_STATUS_OPTIONS = [
  'TASK_STATUS_UNSPECIFIED',
  'TASK_STATUS_OPEN',
  'TASK_STATUS_IN_PROGRESS',
  'TASK_STATUS_IN_REVIEW',
  'TASK_STATUS_BLOCKED',
  'TASK_STATUS_DONE',
  'TASK_STATUS_CANCELLED',
];

export const TASK_PRIORITY_OPTIONS = [
  'TASK_PRIORITY_UNSPECIFIED',
  'TASK_PRIORITY_LOW',
  'TASK_PRIORITY_MEDIUM',
  'TASK_PRIORITY_HIGH',
  'TASK_PRIORITY_URGENT',
  'TASK_PRIORITY_CRITICAL',
];

export const TASK_TYPE_PRESET_OPTIONS = [
  'TASK_TYPE_PRESET_SIMPLE',
  'TASK_TYPE_PRESET_STANDARD',
  'TASK_TYPE_PRESET_ADVANCED',
];

export const TASK_TYPE_COLORS = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#ef4444', label: 'Red' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#64748b', label: 'Slate' },
];

/**
 * The rule offered for "who gets a task of this type", in the order a person
 * reads them: nobody, then the two rules that need no configuration, then the
 * two that name a role.
 *
 * TASK_ASSIGNMENT_RULE_UNSPECIFIED is not offered — it behaves as NONE, and two
 * options that mean the same thing is a choice nobody can make correctly.
 */
export const TASK_ASSIGNMENT_RULE_OPTIONS: TaskAssignmentRule[] = [
  'TASK_ASSIGNMENT_RULE_NONE',
  'TASK_ASSIGNMENT_RULE_CREATOR',
  'TASK_ASSIGNMENT_RULE_PARENT_OWNER',
  'TASK_ASSIGNMENT_RULE_ROLE_HOLDER',
  'TASK_ASSIGNMENT_RULE_ROLE',
];

/** i18n leaf under task.typeForm.assignment.rules for each rule. */
const ASSIGNMENT_RULE_KEY: Record<string, string> = {
  TASK_ASSIGNMENT_RULE_NONE: 'none',
  TASK_ASSIGNMENT_RULE_CREATOR: 'creator',
  TASK_ASSIGNMENT_RULE_PARENT_OWNER: 'parentOwner',
  TASK_ASSIGNMENT_RULE_ROLE_HOLDER: 'roleHolder',
  TASK_ASSIGNMENT_RULE_ROLE: 'role',
};

/** The two rules that point at a role, and so cannot work without one. */
export function requiresAssignmentRole(rule: TaskAssignmentRule | undefined): boolean {
  return rule === 'TASK_ASSIGNMENT_RULE_ROLE_HOLDER' || rule === 'TASK_ASSIGNMENT_RULE_ROLE';
}

/**
 * What a stored rule looks like in the form. An unset rule and NONE are the same
 * behaviour, so the form shows one of them.
 */
export function normalizeAssignmentRule(rule: TaskAssignmentRule | undefined): TaskAssignmentRule {
  if (!rule || rule === 'TASK_ASSIGNMENT_RULE_UNSPECIFIED') return 'TASK_ASSIGNMENT_RULE_NONE';
  return rule;
}

/**
 * The one thing the form can be wrong about. The backend refuses a role rule
 * with no role, so the pages check this before they send and say so in place.
 */
export function assignmentRoleMissing(value: TaskTypeFormData): boolean {
  return requiresAssignmentRole(value.assignment_rule) && !value.assignment_role_id.trim();
}

export function formatLabel(value: string, prefix: string): string {
  return value
    .replace(prefix, '')
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

export interface TaskTypeFormData {
  slug: string;
  display_name: string;
  description: string;
  product_id: string;
  source_entity_type: string;
  default_priority: string;
  initial_status: string;
  completion_status: string;
  enable_time_tracking: boolean;
  enable_checklists: boolean;
  enable_dependencies: boolean;
  enable_progress: boolean;
  enable_watchers: boolean;
  requires_assignee: boolean;
  icon: string;
  color: string;
  default_assignee_id: string;
  default_assignee_name: string;
  /** How a new task of this type finds an assignee before default_assignee. */
  assignment_rule: TaskAssignmentRule;
  /** Entity id of the team role the rule points at; only the role rules use it. */
  assignment_role_id: string;
  default_estimated_hours: string;
  default_tags: string[];
  preset: string;
  is_active: boolean;
  enable_custom_fields: boolean;
  custom_field_ids: string[];
  /**
   * What the forms for this type ask for, and in what order. Edited by
   * TaskTypeFormPropertiesEditor rather than by a field here — it is a list, not
   * a value. Empty means the platform default set, and stays empty all the way
   * to the column: filling in today's default here would freeze it into the row
   * and turn "the platform default" into a copy that never changes again.
   */
  form_properties: TaskTypeProperty[];
  default_pipeline_id: string;
  /**
   * The template new tasks of this type start from. Empty is not "none": it
   * means the type follows whatever its scope offers, which is why the control
   * reads the inherited value instead of a blank.
   */
  default_template_id: string;
  /**
   * The type's lifecycle. Edited by TaskTypeStatusesEditor /
   * TaskTypeTransitionsEditor rather than by the fields below — RegisterTaskType
   * has no channel for either, so they only take effect on update.
   */
  status_definitions: TaskStatusDefinition[];
  transitions: TaskTransitionRule[];
}

export function taskTypeFormDefaults(): TaskTypeFormData {
  return {
    slug: '',
    display_name: '',
    description: '',
    product_id: '',
    source_entity_type: '',
    default_priority: 'TASK_PRIORITY_MEDIUM',
    initial_status: 'TASK_STATUS_OPEN',
    completion_status: 'TASK_STATUS_DONE',
    enable_time_tracking: false,
    enable_checklists: false,
    enable_dependencies: false,
    enable_progress: false,
    enable_watchers: false,
    requires_assignee: false,
    icon: '',
    color: '',
    default_assignee_id: '',
    default_assignee_name: '',
    assignment_rule: 'TASK_ASSIGNMENT_RULE_NONE',
    assignment_role_id: '',
    default_estimated_hours: '',
    default_tags: [],
    preset: 'TASK_TYPE_PRESET_STANDARD',
    is_active: true,
    enable_custom_fields: false,
    custom_field_ids: [],
    form_properties: [],
    default_pipeline_id: '',
    default_template_id: '',
    status_definitions: [],
    transitions: [],
  };
}

/**
 * Small note under a field. The generated Input/Select `helperText` renders at
 * body size — it competes with the label it sits under — so hints are written
 * here instead, at one consistent size across every field on the page.
 */
function FieldHint({ children }: { children: string }) {
  return <p className="mt-1.5 text-xs text-muted-foreground">{children}</p>;
}

interface TaskTypeFormProps {
  value: TaskTypeFormData;
  onChange: <K extends keyof TaskTypeFormData>(field: K, value: TaskTypeFormData[K]) => void;
  /** The slug identifies the type and can't change after creation. */
  isEdit?: boolean;
  /**
   * Set by the page once a save has been refused. A field is only marked wrong
   * after the person has had a turn at it, never the moment it appears.
   */
  submitAttempted?: boolean;
  /**
   * What the scope this type is configured in offers on its own. Unset fields
   * follow these, so the controls state them rather than reading as empty.
   * Null means the scope offers nothing for that field — a real answer, not a
   * missing one.
   */
  inheritedTemplate?: ResolvedReference | null;
  inheritedTemplateName?: string;
  inheritedPipeline?: ResolvedReference | null;
  inheritedPipelineName?: string;
  /** The scope the resolver was asked about — origins compare against it. */
  askedScope?: Entity;
  isResolving?: boolean;
  resolveError?: string | null;
  onRetryResolve?: () => void;
}

export const TaskTypeForm = observer(({
  value,
  onChange,
  isEdit,
  submitAttempted,
  inheritedTemplate = null,
  inheritedTemplateName = '',
  inheritedPipeline = null,
  inheritedPipelineName = '',
  askedScope,
  isResolving,
  resolveError,
  onRetryResolve,
}: TaskTypeFormProps) => {
  const { t } = useI18n();
  const customFieldStore = useTaskCustomField();
  const ruleRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (customFieldStore.items.length === 0 && !customFieldStore.isLoading) {
      customFieldStore.ListCustomFieldDefinitions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customFieldOptions = customFieldStore.items.map((def) => ({
    value: def.entity?.id || '',
    label: def.display_name,
  }));

  const toggleCustomField = (fieldId: string) => {
    const set = new Set(value.custom_field_ids);
    if (set.has(fieldId)) set.delete(fieldId); else set.add(fieldId);
    onChange('custom_field_ids', Array.from(set));
  };

  const tagsText = value.default_tags.join(', ');

  const activeRule = normalizeAssignmentRule(value.assignment_rule);
  const roleMissing = assignmentRoleMissing(value);

  // Changing the rule away from a role rule drops the role with it: a role left
  // behind in the payload would be saved against a rule that never reads it,
  // and would reappear as the answer if the rule were ever switched back.
  const selectRule = (rule: TaskAssignmentRule) => {
    if (rule === activeRule) return;
    onChange('assignment_rule', rule);
    if (!requiresAssignmentRole(rule) && value.assignment_role_id) {
      onChange('assignment_role_id', '');
    }
  };

  const handleRuleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const delta = e.key === 'ArrowDown' || e.key === 'ArrowRight'
      ? 1
      : e.key === 'ArrowUp' || e.key === 'ArrowLeft'
        ? -1
        : 0;
    if (!delta) return;
    e.preventDefault();
    const count = TASK_ASSIGNMENT_RULE_OPTIONS.length;
    const nextIndex = (index + delta + count) % count;
    selectRule(TASK_ASSIGNMENT_RULE_OPTIONS[nextIndex]);
    ruleRefs.current[nextIndex]?.focus();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('task.typeForm.sections.basics.title')}</CardTitle>
          <CardDescription>{t('task.typeForm.sections.basics.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              className="mb-0"
              id="type-display-name"
              label={t('task.typeForm.displayName')}
              value={value.display_name}
              onChange={(e) => onChange('display_name', e.target.value)}
              placeholder={t('task.typeForm.displayNamePlaceholder')}
              autoCapitalize="off"
            />
            <div>
              <Input
                className="mb-0"
                id="type-slug"
                label={t('task.typeForm.slug')}
                value={value.slug}
                onChange={(e) => onChange('slug', e.target.value)}
                placeholder="product.entity_type"
                disabled={isEdit}
                autoCapitalize="off"
              />
              <FieldHint>{t('task.typeForm.slugHint')}</FieldHint>
            </div>
          </div>

          <Input
            className="mb-0"
            id="type-description"
            label={t('task.typeForm.description')}
            value={value.description}
            onChange={(e) => onChange('description', e.target.value)}
            placeholder={t('task.typeForm.descriptionPlaceholder')}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Icon and colour are one decision about one badge, so one control
                carries both instead of eight large swatches sitting inline. */}
            <div>
              <span className="label">{t('task.typeForm.badge')}</span>
              <IconPicker
                value={value.icon}
                onChange={(v) => onChange('icon', v)}
                colors={TASK_TYPE_COLORS}
                color={value.color}
                onColorChange={(v) => onChange('color', v)}
                aria-label={t('task.typeForm.badge')}
              />
              <FieldHint>{t('task.typeForm.badgeHint')}</FieldHint>
            </div>

            <div>
              <Input
                className="mb-0"
                id="type-source-entity"
                label={t('task.typeForm.sourceEntityType')}
                value={value.source_entity_type}
                onChange={(e) => onChange('source_entity_type', e.target.value)}
                autoCapitalize="off"
              />
              <FieldHint>{t('task.typeForm.sourceEntityTypeHint')}</FieldHint>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <ToggleSwitch
              label={t('common.active')}
              description={t('task.typeForm.activeHint')}
              checked={value.is_active}
              onChange={(v) => onChange('is_active', v)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('task.typeForm.sections.defaults.title')}</CardTitle>
          <CardDescription>{t('task.typeForm.sections.defaults.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Select
                label={t('task.typeForm.preset')}
                value={value.preset}
                onChange={(e) => onChange('preset', e.target.value)}
                options={TASK_TYPE_PRESET_OPTIONS.map((p) => ({ value: p, label: formatLabel(p, 'TASK_TYPE_PRESET_') }))}
              />
              <FieldHint>{t('task.typeForm.presetHint')}</FieldHint>
            </div>
            <Select
              label={t('task.typeForm.defaultPriority')}
              value={value.default_priority}
              onChange={(e) => onChange('default_priority', e.target.value)}
              options={TASK_PRIORITY_OPTIONS.map((p) => ({ value: p, label: formatLabel(p, 'TASK_PRIORITY_') }))}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label={t('task.typeForm.initialStatus')}
              value={value.initial_status}
              onChange={(e) => onChange('initial_status', e.target.value)}
              options={TASK_STATUS_OPTIONS.map((s) => ({ value: s, label: formatLabel(s, 'TASK_STATUS_') }))}
            />
            <Select
              label={t('task.typeForm.completionStatus')}
              value={value.completion_status}
              onChange={(e) => onChange('completion_status', e.target.value)}
              options={TASK_STATUS_OPTIONS.map((s) => ({ value: s, label: formatLabel(s, 'TASK_STATUS_') }))}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              className="mb-0"
              id="type-estimated-hours"
              label={t('task.typeForm.defaultEstimatedHours')}
              inputMode="decimal"
              value={value.default_estimated_hours}
              onChange={(e) => onChange('default_estimated_hours', e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="8"
            />
            <div>
              <Input
                className="mb-0"
                id="type-default-tags"
                label={t('task.typeForm.defaultTags')}
                value={tagsText}
                onChange={(e) => onChange('default_tags', parseTags(e.target.value))}
                placeholder="backend, urgent"
              />
              <FieldHint>{t('task.typeForm.defaultTagsHint')}</FieldHint>
            </div>
          </div>

          {/* Who gets the task. The rule and the fixed default assignee are the
              same decision at two levels of specificity, so they sit in one
              block in the order they are consulted — rule first, fixed assignee
              underneath as what happens when the rule finds nobody. */}
          <div className="space-y-4 border-t border-border pt-4">
            <div>
              <h3 className="text-sm font-medium text-foreground">
                {t('task.typeForm.assignment.title')}
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t('task.typeForm.assignment.description')}
              </p>
            </div>

            <div
              role="radiogroup"
              aria-label={t('task.typeForm.assignment.title')}
              className="space-y-2"
            >
              {TASK_ASSIGNMENT_RULE_OPTIONS.map((rule, index) => {
                const key = ASSIGNMENT_RULE_KEY[rule];
                const isSelected = rule === activeRule;
                const showsRole = isSelected && requiresAssignmentRole(rule);
                return (
                  <div
                    key={rule}
                    className={`rounded-lg border transition-colors ${
                      isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-input'
                    }`}
                  >
                    <button
                      ref={(el) => { ruleRefs.current[index] = el; }}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      tabIndex={isSelected ? 0 : -1}
                      onClick={() => selectRule(rule)}
                      onKeyDown={(e) => handleRuleKeyDown(e, index)}
                      className="flex w-full items-start gap-3 rounded-lg p-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span
                        aria-hidden="true"
                        className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border transition-colors ${
                          isSelected ? 'border-primary' : 'border-input'
                        }`}
                      >
                        {isSelected && <span className="h-2 w-2 rounded-full bg-primary" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground">
                          {t(`task.typeForm.assignment.rules.${key}.label`)}
                        </span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                          {t(`task.typeForm.assignment.rules.${key}.description`)}
                        </span>
                      </span>
                    </button>

                    {/* The role belongs to the rule that reads it, so it is
                        inside that option rather than a field floating beside
                        the group — choose another rule and it goes with it. */}
                    {showsRole && (
                      <div className="border-t border-primary/20 p-3">
                        <RolePicker
                          id="type-assignment-role"
                          label={t('task.typeForm.assignment.role')}
                          value={value.assignment_role_id}
                          onChange={(roleId) => onChange('assignment_role_id', roleId)}
                          error={submitAttempted && roleMissing ? t('task.typeForm.assignment.roleRequired') : undefined}
                        />
                        <FieldHint>{t('task.typeForm.assignment.roleHint')}</FieldHint>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <UserPicker
                  label={t('task.typeForm.defaultAssignee')}
                  value={value.default_assignee_id}
                  onChange={(userId, name) => {
                    onChange('default_assignee_id', userId);
                    onChange('default_assignee_name', name);
                  }}
                />
                <FieldHint>{t('task.typeForm.defaultAssigneeHint')}</FieldHint>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('task.typeForm.sections.features.title')}</CardTitle>
          <CardDescription>{t('task.typeForm.sections.features.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
            <ToggleSwitch
              label={t('task.typeForm.enableTimeTracking')}
              checked={value.enable_time_tracking}
              onChange={(v) => onChange('enable_time_tracking', v)}
            />
            <ToggleSwitch
              label={t('task.typeForm.enableChecklists')}
              checked={value.enable_checklists}
              onChange={(v) => onChange('enable_checklists', v)}
            />
            <ToggleSwitch
              label={t('task.typeForm.enableDependencies')}
              checked={value.enable_dependencies}
              onChange={(v) => onChange('enable_dependencies', v)}
            />
            <ToggleSwitch
              label={t('task.typeForm.enableProgress')}
              checked={value.enable_progress}
              onChange={(v) => onChange('enable_progress', v)}
            />
            <ToggleSwitch
              label={t('task.typeForm.enableWatchers')}
              checked={value.enable_watchers}
              onChange={(v) => onChange('enable_watchers', v)}
            />
            <ToggleSwitch
              label={t('task.typeForm.requiresAssignee')}
              checked={value.requires_assignee}
              onChange={(v) => onChange('requires_assignee', v)}
            />
            <ToggleSwitch
              label={t('task.typeForm.enableCustomFields')}
              checked={value.enable_custom_fields}
              onChange={(v) => onChange('enable_custom_fields', v)}
            />
          </div>

          {value.enable_custom_fields && (
            <div className="space-y-2 border-t border-border pt-4">
              <span className="label">{t('task.customFields')}</span>
              <ChipMultiSelect
                options={customFieldOptions}
                selected={value.custom_field_ids}
                onToggle={toggleCustomField}
                emptyLabel={t('task.typeForm.noCustomFields')}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('task.typeForm.sections.automation.title')}</CardTitle>
          <CardDescription>{t('task.typeForm.sections.automation.description')}</CardDescription>
        </CardHeader>
        {/* Both of these inherit. Leaving one blank is not "none" — it hands
            the field to the scope, and the control says which value that is.
            Neither field gets an "inherit" switch: emptiness already carries
            that meaning, and a switch beside it would be a second answer to a
            question the resolution chain has already settled. */}
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <TaskTemplatePicker
                id="type-default-template"
                label={t('task.typeForm.defaultTemplate')}
                value={value.default_template_id}
                onChange={(id) => onChange('default_template_id', id)}
                placeholder={inheritedPlaceholder(t, inheritedTemplate, inheritedTemplateName)}
              />
              <FieldHint>{t('task.typeForm.defaultTemplateHint')}</FieldHint>
              <InheritedValueNote
                field="template"
                inherited={inheritedTemplate}
                inheritedName={inheritedTemplateName}
                askedScope={askedScope}
                isOverridden={!!value.default_template_id}
                isResolving={isResolving}
                resolveError={resolveError}
                onRetryResolve={onRetryResolve}
              />
            </div>

            <div>
              <PipelinePicker
                label={t('task.typeForm.defaultPipeline')}
                value={value.default_pipeline_id}
                onChange={(id) => onChange('default_pipeline_id', id)}
                placeholder={inheritedPlaceholder(t, inheritedPipeline, inheritedPipelineName)}
              />
              <FieldHint>{t('task.typeForm.defaultPipelineHint')}</FieldHint>
              <InheritedValueNote
                field="pipeline"
                inherited={inheritedPipeline}
                inheritedName={inheritedPipelineName}
                askedScope={askedScope}
                isOverridden={!!value.default_pipeline_id}
                isResolving={isResolving}
                resolveError={resolveError}
                onRetryResolve={onRetryResolve}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});
